/**
 * Currency Migration Utility
 *
 * Helps migrate existing investments to include currency field
 * based on user's country/currency preference
 */

import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Migrate user's investments to include currency field
 * Sets currency based on user's country:
 * - US users: USD
 * - UK users: GBP
 * - Others: USD (default)
 *
 * @param {string} userId - User ID
 * @param {string} userCountry - User's country code (US, UK)
 * @returns {Promise<Object>} Migration result
 */
export const migrateInvestmentsCurrency = async (userId, userCountry) => {
  try {
    console.log(`🔄 Starting currency migration for user ${userId}...`);

    // Determine default currency based on country
    const defaultCurrency = userCountry === 'UK' ? 'GBP' : 'USD';

    // Get user profile document
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      throw new Error('User document not found');
    }

    const userData = userDoc.data();
    const investments = userData.investments || [];

    // Check if migration is needed
    const needsMigration = investments.some(inv => !inv.currency);

    if (!needsMigration) {
      console.log('✅ No migration needed - all investments already have currency field');
      return {
        success: true,
        migrated: false,
        message: 'All investments already have currency field'
      };
    }

    // Migrate investments
    const migratedInvestments = investments.map(investment => {
      if (!investment.currency) {
        // Add currency field based on user's country
        return {
          ...investment,
          currency: defaultCurrency
        };
      }
      return investment;
    });

    // Count how many were updated
    const migratedCount = migratedInvestments.filter(
      (inv, idx) => inv.currency !== investments[idx].currency
    ).length;

    // Update Firestore
    await updateDoc(userDocRef, {
      investments: migratedInvestments
    });

    console.log(`✅ Successfully migrated ${migratedCount} investments to ${defaultCurrency}`);

    return {
      success: true,
      migrated: true,
      count: migratedCount,
      defaultCurrency,
      message: `Successfully migrated ${migratedCount} investments to ${defaultCurrency}`
    };
  } catch (error) {
    console.error('❌ Error migrating investments:', error);
    return {
      success: false,
      migrated: false,
      error: error.message
    };
  }
};

/**
 * Check if user's investments need currency migration
 *
 * @param {Array} investments - User's investments array
 * @returns {boolean} True if migration is needed
 */
export const checkMigrationNeeded = (investments) => {
  if (!investments || investments.length === 0) {
    return false;
  }

  return investments.some(inv => !inv.currency || inv.currency === null || inv.currency === '');
};

/**
 * Get migration status for UI display
 *
 * @param {Array} investments - User's investments array
 * @returns {Object} Status object
 */
export const getMigrationStatus = (investments) => {
  if (!investments || investments.length === 0) {
    return {
      needed: false,
      total: 0,
      withCurrency: 0,
      withoutCurrency: 0,
      percentage: 100
    };
  }

  const total = investments.length;
  const withCurrency = investments.filter(inv => inv.currency).length;
  const withoutCurrency = total - withCurrency;
  const percentage = (withCurrency / total) * 100;

  return {
    needed: withoutCurrency > 0,
    total,
    withCurrency,
    withoutCurrency,
    percentage: Math.round(percentage)
  };
};

/**
 * Migrate single investment to add currency
 * Used for client-side migration without Firestore update
 *
 * @param {Object} investment - Investment object
 * @param {string} defaultCurrency - Default currency to use
 * @returns {Object} Investment with currency field
 */
export const migrateInvestment = (investment, defaultCurrency = 'USD') => {
  if (investment.currency) {
    return investment;
  }

  return {
    ...investment,
    currency: defaultCurrency
  };
};

/**
 * Migrate array of investments (client-side)
 *
 * @param {Array} investments - Array of investments
 * @param {string} defaultCurrency - Default currency to use
 * @returns {Array} Migrated investments
 */
export const migrateInvestmentsArray = (investments, defaultCurrency = 'USD') => {
  if (!investments || investments.length === 0) {
    return [];
  }

  return investments.map(inv => migrateInvestment(inv, defaultCurrency));
};

/**
 * Show migration prompt to user
 * Returns migration prompt data for UI component
 *
 * @param {number} count - Number of investments needing migration
 * @param {string} currency - Currency they will be migrated to
 * @returns {Object} Prompt data
 */
export const getMigrationPrompt = (count, currency) => {
  return {
    title: 'Currency Update Available',
    message: `We've added multi-currency support! ${count} of your investments need to be assigned a currency.`,
    detail: `By default, they'll be set to ${currency} based on your profile. You can change individual currencies later.`,
    action: 'Update Now',
    cancelAction: 'Remind Me Later'
  };
};

export default {
  migrateInvestmentsCurrency,
  checkMigrationNeeded,
  getMigrationStatus,
  migrateInvestment,
  migrateInvestmentsArray,
  getMigrationPrompt
};
